import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { useTranslation } from 'localization';
import { resolveLabel } from '../../../utils/resolveLabel';
import type { ContextOption } from '../../molecules/ContextSelectorMenu/ContextSelectorMenuMolecule';
import SearchBarAtom from '../SearchBar/SearchBarAtom';
import ContextChipAtom from '../ContextChip/ContextChipAtom';
import { dropdownAccessibility } from '../Dropdown/dropdownAccessibility';
import './current-sphere-dropdown.scss';

interface DropdownOption {
  name: string;
  code: string | number;
}

interface SphereDropdownProps {
  value: string;
  options: DropdownOption[];
  onChange: (e: { value: string | number }) => void;
  placeholder?: string;
  optionLabel?: string;
  filter?: boolean;
  className?: string;
  valueTemplate?: (option: DropdownOption) => JSX.Element;
  itemTemplate?: (option: DropdownOption) => JSX.Element;
  dropdownOpen?: boolean;
  contexts?: ContextOption[];
  selectedContext?: string | null;
  onContextSelect?: (systemName: string | null) => void;
  contextLoading?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  isSearching?: boolean;
  searchError?: boolean;
  /** Accessible name; defaults to the placeholder. */
  ariaLabel?: string;
}

const CurrentSphereSelect: React.FC<SphereDropdownProps> = ({
  value,
  options,
  onChange,
  placeholder = "Select an option",
  optionLabel = "label",
  filter = false,
  className = "",
  valueTemplate,
  itemTemplate,
  dropdownOpen = false,
  contexts,
  selectedContext,
  onContextSelect,
  contextLoading = false,
  searchQuery = '',
  onSearchChange,
  isSearching = false,
  searchError = false,
  ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(dropdownOpen);
  const [showBackButton, setShowBackButton] = useState(false);
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(false);
  const [showNoMoreResults, setShowNoMoreResults] = useState(false);
  const { t, i18n } = useTranslation();
  const [showContextMenu, setShowContextMenu] = useState(false);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const contextBtnRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<Dropdown>(null);
  const suppressHideRef = useRef(false);

  const a11y = dropdownAccessibility({ ariaLabel, placeholder });

  const hasContextSupport = Boolean(contexts && contexts.length > 0 && onContextSelect);

  useEffect(() => {
    setIsOpen(dropdownOpen);
  }, [dropdownOpen]);

  const handleScroll = useCallback(() => {
    const dropdownPanel = document.querySelector('.custom-sphere-dropdown-panel .p-dropdown-items-wrapper') as HTMLElement;
    if (dropdownPanel) {
      const { scrollTop, scrollHeight, clientHeight } = dropdownPanel;

      setShowBackButton(scrollTop > 0);
      setIsNextButtonDisabled(scrollTop + clientHeight >= scrollHeight - 1);
      setShowNoMoreResults(scrollTop + clientHeight >= scrollHeight - 1);
    }
  }, []);

  const handleNextClick = () => {
    const dropdownPanel = document.querySelector('.custom-sphere-dropdown-panel .p-dropdown-items-wrapper') as HTMLElement;
    if (dropdownPanel) {
      dropdownPanel.scrollBy({ top: 195, behavior: 'smooth' });
    }
  };

  const handleBackClick = () => {
    const dropdownPanel = document.querySelector('.custom-sphere-dropdown-panel .p-dropdown-items-wrapper') as HTMLElement;
    if (dropdownPanel) {
      dropdownPanel.scrollBy({ top: -195, behavior: 'smooth' });
    }
  };

  const appendDropdownElements = useCallback(() => {
    const dropdownPanel = document.querySelector('.custom-sphere-dropdown-panel') as HTMLElement;
    if (!dropdownPanel) return;

    const itemsWrapper = dropdownPanel.querySelector('.p-dropdown-items-wrapper');
    if (itemsWrapper) {
      itemsWrapper.addEventListener('scroll', handleScroll);
    }


    let footerDiv = dropdownPanel.querySelector('.dropdown-footer-buttons') as HTMLElement;
    if (!footerDiv) {
      footerDiv = document.createElement('div');
      footerDiv.className = 'dropdown-footer-buttons';

      const backButton = document.createElement('button');
      backButton.className = 'back-button';
      backButton.innerHTML = `<span class="icon-circle"><i class="pi pi-angle-left"></i></span> <span>${t('common.pagination.back')}</span>`;
      backButton.onclick = handleBackClick;

      const nextButton = document.createElement('button');
      nextButton.className = 'next-button';
      nextButton.innerHTML = `<span>${t('common.pagination.next')}</span> <span class="icon-circle"><i class="pi pi-angle-right"></i></span>`;
      nextButton.onclick = handleNextClick;

      footerDiv.appendChild(backButton);
      footerDiv.appendChild(nextButton);

      dropdownPanel.appendChild(footerDiv);
    }

    const backButton = dropdownPanel.querySelector('.back-button') as HTMLButtonElement;
    if (backButton) {
      backButton.disabled = !showBackButton;
    }

    const nextButton = dropdownPanel.querySelector('.next-button') as HTMLButtonElement;
    if (nextButton) {
      nextButton.disabled = isNextButtonDisabled;
    }


    if (options.length === 0) {
      footerDiv.style.display = 'none';
    } else {
      footerDiv.style.display = 'flex';
    }
  }, [handleScroll, showBackButton, isNextButtonDisabled, showNoMoreResults, options.length]);

  useEffect(() => {
    if (isOpen) {
      appendDropdownElements();
    }
  }, [isOpen, appendDropdownElements]);

  // Close context menu when main dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setShowContextMenu(false);
    }
  }, [isOpen]);

  // Close context menu on outside click (keeps main dropdown open)
  useEffect(() => {
    if (!showContextMenu) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node) &&
        contextBtnRef.current && !contextBtnRef.current.contains(e.target as Node)
      ) {
        setShowContextMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showContextMenu]);

  const sortedContexts = useMemo(() => {
    if (!contexts) return [];
    return [...contexts].sort((a, b) => {
      const nameA = resolveLabel(a.name, i18n.language);
      const nameB = resolveLabel(b.name, i18n.language);
      return nameA.localeCompare(nameB);
    });
  }, [contexts, i18n.language]);

  const selectedContextLabel = useMemo(() => {
    if (!selectedContext) return t('contextSelector.global');
    const ctx = contexts?.find((c) => c.systemName === selectedContext);
    return ctx ? resolveLabel(ctx.name, i18n.language) : t('contextSelector.global');
  }, [selectedContext, contexts, t, i18n.language]);

  const selectedContextIcon = useMemo(() => {
    if (!selectedContext) return 'pi-globe';
    const ctx = contexts?.find((c) => c.systemName === selectedContext);
    return ctx?.icon || 'pi-filter';
  }, [selectedContext, contexts]);

  const suppressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleContextSelect = useCallback((systemName: string | null) => {
    // Suppress dropdown close for a short period to survive multiple onHide
    // calls that PrimeReact may fire during re-renders
    suppressHideRef.current = true;
    if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
    suppressTimerRef.current = setTimeout(() => {
      suppressHideRef.current = false;
    }, 500);
    onContextSelect?.(systemName);
    setShowContextMenu(false);
  }, [onContextSelect]);

  const renderFilterHeader = useCallback(() => {
    return (
      <div className="custom-filter-header">
        <SearchBarAtom
          value={searchQuery}
          onChange={(val) => {
            if (!val && searchQuery) {
              // Clearing search — suppress dropdown hide since the clear button
              // will unmount and cause a focus loss that PrimeReact interprets as blur
              suppressHideRef.current = true;
              if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
              suppressTimerRef.current = setTimeout(() => {
                suppressHideRef.current = false;
              }, 500);
            }
            onSearchChange?.(val);
          }}
          placeholder={t('sphereDropdown.searchPlaceholder')}
          isSearching={isSearching}
          rightAdornment={
            hasContextSupport ? (
              <div
                className="custom-filter-header__context-wrapper"
                role="presentation"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <ContextChipAtom
                  icon={selectedContextIcon}
                  label={selectedContextLabel}
                  loading={contextLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowContextMenu(prev => !prev);
                  }}
                  buttonRef={contextBtnRef}
                />
                {showContextMenu && (
                  <div
                    ref={contextMenuRef}
                    className="context-overlay-panel context-overlay-panel--inline"
                  >
                    <div
                      className="context-overlay-panel__list"
                      role="listbox"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                          e.preventDefault();
                          const items = (e.currentTarget as HTMLElement).querySelectorAll('[role="option"]');
                          const current = document.activeElement;
                          const idx = Array.from(items).indexOf(current as Element);
                          const next = e.key === 'ArrowDown'
                            ? items[Math.min(idx + 1, items.length - 1)]
                            : items[Math.max(idx - 1, 0)];
                          (next as HTMLElement)?.focus();
                        } else if (e.key === 'Escape') {
                          e.stopPropagation();
                          setShowContextMenu(false);
                        }
                      }}
                    >
                      <div
                        className={`context-overlay-panel__item ${selectedContext ? '' : 'context-overlay-panel__item--selected'}`}
                        onClick={() => handleContextSelect(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleContextSelect(null);
                          }
                        }}
                        role="option"
                        aria-selected={!selectedContext}
                        tabIndex={0}
                        data-testid="context-option-global"
                      >
                        <i className="pi pi-globe context-overlay-panel__item-icon" />
                        <span className="context-overlay-panel__item-label">
                          {t('contextSelector.global')}
                        </span>
                        {!selectedContext && (
                          <i className="pi pi-check context-overlay-panel__item-check" />
                        )}
                      </div>
                      {sortedContexts.length > 0 && (
                        <div className="context-overlay-panel__separator" />
                      )}
                      {sortedContexts.map((ctx) => {
                        const isSelected = selectedContext === ctx.systemName;
                        const label = resolveLabel(ctx.name, i18n.language);
                        return (
                          <div
                            key={ctx.systemName}
                            className={`context-overlay-panel__item ${isSelected ? 'context-overlay-panel__item--selected' : ''}`}
                            onClick={() => handleContextSelect(ctx.systemName)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleContextSelect(ctx.systemName);
                              }
                            }}
                            role="option"
                            aria-selected={isSelected}
                            tabIndex={0}
                            data-testid={`context-option-${ctx.systemName}`}
                          >
                            <i className={`pi ${ctx.icon || 'pi-filter'} context-overlay-panel__item-icon`} />
                            <span className="context-overlay-panel__item-label">{label}</span>
                            {isSelected && (
                              <i className="pi pi-check context-overlay-panel__item-check" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : undefined
          }
        />
      </div>
    );
  }, [
    hasContextSupport, contextLoading, selectedContextIcon, selectedContextLabel,
    selectedContext, sortedContexts, searchQuery, isSearching, onSearchChange,
    handleContextSelect, showContextMenu, t, i18n.language,
  ]);

  const useCustomHeader = hasContextSupport || onSearchChange;

  const emptyMessage = useMemo(() => {
    if (searchError) return t('sphereDropdown.searchError');
    if (searchQuery.trim()) return t('sphereDropdown.noSearchResults');
    return t('sphereDropdown.emptyContext');
  }, [searchError, searchQuery, t]);

  return (
    <Dropdown
      ref={dropdownRef}
      value={value}
      onChange={onChange}
      options={options}
      optionLabel={optionLabel}
      placeholder={placeholder}
      filter={useCustomHeader ? true : filter}
      filterTemplate={useCustomHeader ? renderFilterHeader : undefined}
      onFilter={() => { /* prevent PrimeReact built-in filtering */ }}
      valueTemplate={valueTemplate}
      itemTemplate={itemTemplate}
      panelClassName="custom-sphere-dropdown-panel"
      className={`custom-sphere-dropdown ${className}`}
      onShow={() => setIsOpen(true)}
      onHide={() => {
        if (suppressHideRef.current) {
          requestAnimationFrame(() => {
            dropdownRef.current?.show();
          });
          return;
        }
        setIsOpen(false);
      }}
      dropdownIcon={isOpen ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
      emptyMessage={emptyMessage}
      {...a11y.nameAttrs}
      ariaLabel={a11y.ariaLabel}
      ariaLabelledBy={a11y.ariaLabelledBy}
      pt={a11y.pt}
    />
  );
};

export default CurrentSphereSelect;
